/**
 * The mark + un-mark dialogs for "Absender fuer mich privat markieren" (P2 hardening 04).
 *
 * The copy makes the per-user vs firm-wide distinction explicit ("Gilt nur fuer Sie"), so a
 * per-user private is never confused with the per-mail firm-wide Privat-Markierung or the admin
 * firm-wide block. Separate from the per-mail Privat-Markierung in every way.
 */

import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { SenderPrivatePreviewResponse } from '@brokly/shared';
import { Button } from '../../components/ui/Button';
import { ApiError } from '../../lib/api-client';
import { useMarkSenderPrivate, useUnmarkSenderPrivate } from './useSenderPrivate';

/** §6.2 - the mark dialog. "Ab jetzt" vs "Ab jetzt und alle bisherigen". */
export function SenderPrivateMarkDialog({
  address,
  preview,
  previewLoading,
  onClose,
}: {
  address: string;
  preview: SenderPrivatePreviewResponse | undefined;
  previewLoading: boolean;
  onClose: () => void;
}) {
  const mark = useMarkSenderPrivate();
  const [includePast, setIncludePast] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pastCount = preview?.past_count ?? 0;
  const noPast = !previewLoading && pastCount === 0;
  const isInternal = preview?.is_internal_sender === true;
  const showNewsletterHint = !isInternal;

  return (
    <div
      role="dialog"
      aria-label="Absender privat markieren"
      className="mt-3 flex flex-col gap-2 rounded-md border border-border bg-muted/40 p-3 text-sm"
    >
      <p className="font-medium text-foreground">{address} für Sie privat markieren?</p>

      <fieldset className="flex flex-col gap-2">
        <label className="flex items-start gap-2">
          <input
            type="radio"
            name="sp-scope"
            className="mt-1"
            checked={!includePast}
            onChange={() => setIncludePast(false)}
          />
          <span>
            <span className="font-medium text-foreground">Ab jetzt</span>
            <span className="block text-xs text-muted-foreground">
              Neue E-Mails dieses Absenders erscheinen nicht mehr in Ihrem Posteingang.
            </span>
          </span>
        </label>
        <label className="flex items-start gap-2">
          <input
            type="radio"
            name="sp-scope"
            className="mt-1"
            checked={includePast}
            disabled={noPast}
            onChange={() => setIncludePast(true)}
          />
          <span>
            <span className="font-medium text-foreground">
              Ab jetzt und alle bisherigen{' '}
              {previewLoading ? (
                <Loader2 className="inline size-3 animate-spin" />
              ) : noPast ? (
                <span className="text-muted-foreground">(keine bisherigen E-Mails)</span>
              ) : (
                <span className="text-muted-foreground">(bis zu {pastCount} E-Mails)</span>
              )}
            </span>
            <span className="block text-xs text-muted-foreground">
              Auch bereits erhaltene E-Mails werden in Ihrem Posteingang ausgeblendet.
            </span>
          </span>
        </label>
      </fieldset>

      <ul className="list-disc pl-4 text-xs text-muted-foreground">
        <li>Gilt nur für Sie. Kolleginnen und Kollegen sehen die E-Mails weiterhin.</li>
        <li>Die E-Mails bleiben in der Kommunikation und in der Suche erreichbar.</li>
        <li>Rückgängig: hier in der Maske oder unter Einstellungen › Private Absender.</li>
        {showNewsletterHint && (
          <li>Newsletter? Dann lieber «Ablehnen/Archivieren»: das entfernt die E-Mail für alle.</li>
        )}
      </ul>
      {isInternal && (
        <p className="text-xs text-status-warning" role="note">
          ⚠ {address} gehört zu Ihrer eigenen Firma. Interne E-Mails dieser Person werden für Sie
          ausgeblendet.
        </p>
      )}
      {error && (
        <p role="alert" className="text-xs text-status-danger">
          {error}
        </p>
      )}

      <div className="flex gap-2">
        <Button
          size="sm"
          disabled={mark.isPending}
          onClick={() => {
            setError(null);
            mark.mutate(
              { address, include_past: includePast },
              {
                onSuccess: () => onClose(),
                onError: (err) =>
                  setError(err instanceof ApiError ? err.message : 'Konnte nicht markiert werden.'),
              },
            );
          }}
        >
          {mark.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
          Privat markieren
        </Button>
        <Button size="sm" variant="outline" disabled={mark.isPending} onClick={onClose}>
          Abbrechen
        </Button>
      </div>
    </div>
  );
}

/** §6.3 - the un-mark dialog. "Ab jetzt wieder anzeigen" vs "Alle wieder anzeigen". */
export function SenderPrivateUnmarkDialog({
  address,
  rowId,
  onClose,
}: {
  address: string;
  rowId: string;
  onClose: () => void;
}) {
  const unmark = useUnmarkSenderPrivate();
  const [revealPast, setRevealPast] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <div
      role="dialog"
      aria-label="Privat-Markierung aufheben"
      className="mt-3 flex flex-col gap-2 rounded-md border border-border bg-muted/40 p-3 text-sm"
    >
      <p className="font-medium text-foreground">{address} nicht mehr privat?</p>
      <fieldset className="flex flex-col gap-2">
        <label className="flex items-start gap-2">
          <input
            type="radio"
            name="sp-unmark"
            className="mt-1"
            checked={!revealPast}
            onChange={() => setRevealPast(false)}
          />
          <span>
            <span className="font-medium text-foreground">Ab jetzt wieder anzeigen</span>
            <span className="block text-xs text-muted-foreground">
              Bisher ausgeblendete E-Mails bleiben ausgeblendet.
            </span>
          </span>
        </label>
        <label className="flex items-start gap-2">
          <input
            type="radio"
            name="sp-unmark"
            className="mt-1"
            checked={revealPast}
            onChange={() => setRevealPast(true)}
          />
          <span className="font-medium text-foreground">Alle wieder anzeigen, auch die bisherigen</span>
        </label>
      </fieldset>
      {error && (
        <p role="alert" className="text-xs text-status-danger">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <Button
          size="sm"
          disabled={unmark.isPending}
          onClick={() => {
            setError(null);
            unmark.mutate(
              { id: rowId, reveal_past: revealPast },
              {
                onSuccess: () => onClose(),
                onError: (err) =>
                  setError(err instanceof ApiError ? err.message : 'Konnte nicht aufgehoben werden.'),
              },
            );
          }}
        >
          {unmark.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
          Aufheben
        </Button>
        <Button size="sm" variant="outline" disabled={unmark.isPending} onClick={onClose}>
          Abbrechen
        </Button>
      </div>
    </div>
  );
}
